import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Heart, UserPlus } from "lucide-react";
import Navbar from "../components/Navbar";
import PosterCard from "../components/PosterCard";
import WheelBrowser, { cardRow } from "../components/clickwheel/WheelBrowser";
import { useTheme } from "../context/ThemeContext";
import Disclaimer from "../components/Disclaimer";
import api from "../lib/api";
import { GENRES } from "../lib/genres";
import { FilterChip } from "./GenreBrowse";
import LovedByStack, { LovedByDialog } from "../components/LovedByStack";

function EmptyNudge() {
  return (
    <div className="mx-auto mt-16 max-w-md text-center" data-testid="friends-favorites-empty">
      <Heart size={36} className="mx-auto text-[var(--accent)]" />
      <p className="mt-4 text-lg font-semibold text-[var(--text-primary)]">Your friends haven't loved anything yet</p>
      <p className="mt-2 text-sm text-[var(--text-secondary)]">Add friends and the titles they favorite will show up here.</p>
      <Link to="/friends" data-testid="friends-favorites-find-friends" className="genre-pill genre-pill-active mt-6 inline-flex items-center gap-2">
        <UserPlus size={15} /> Find Friends
      </Link>
    </div>
  );
}

export default function FriendsFavorites() {
  const wheel = useTheme().skin === "clickwheel";
  const navigate = useNavigate();
  const [items, setItems] = useState(null);
  const [mediaType, setMediaType] = useState("all");
  const [who, setWho] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);
    api.get("/catalog/loved-by-friends?limit=60")
      .then(({ data }) => setItems(data.items || []))
      .catch(() => setItems([]));
  }, []);

  const shown = (items || []).filter((it) => mediaType === "all" || it.media_type === mediaType);

  return (
    <div className="min-h-screen bg-[var(--bg-base)]">
      <Navbar />
      <main className="px-4 lg:px-14 pt-6 pb-20" data-testid="friends-favorites-page">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <p className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-widest text-[var(--accent-hover)]"><Heart size={12} fill="currentColor" /> Friends' Favorites</p>
            <h1 className="font-head text-4xl sm:text-5xl tracking-wide text-[var(--text-primary)]" data-testid="friends-favorites-title">Loved by Friends</h1>
          </div>
          <label className="flex items-center gap-2 text-sm text-[var(--text-secondary)]">
            <span className="hidden sm:inline">Jump to genre</span>
            <select
              value=""
              onChange={(e) => e.target.value && navigate(`/genre/${e.target.value}`)}
              data-testid="genre-switcher"
              className="rounded-[var(--card-radius)] border border-[var(--border-subtle)] bg-[var(--bg-card)] px-3 py-2 text-sm text-[var(--text-primary)] outline-none focus:border-[var(--accent)]"
            >
              <option value="">Friends' Favorites</option>
              {GENRES.map((g) => <option key={g.slug} value={g.slug}>{g.name}</option>)}
            </select>
          </label>
        </div>

        {items === null && <p className="mt-10 text-[var(--text-secondary)]" data-testid="friends-favorites-loading">Loading your friends' favorites…</p>}
        {items && items.length === 0 && <EmptyNudge />}

        {items && items.length > 0 && (
          <>
            <div className="mt-5 flex flex-wrap items-center gap-2" data-testid="genre-media-filters">
              <span className="mr-1 text-xs font-semibold uppercase tracking-widest text-[var(--text-muted)]">Type</span>
              <FilterChip label="All" active={mediaType === "all"} onClick={() => setMediaType("all")} testid="genre-media-all" />
              <FilterChip label="Movies" active={mediaType === "movie"} onClick={() => setMediaType("movie")} testid="genre-media-movie" />
              <FilterChip label="TV Shows" active={mediaType === "tv"} onClick={() => setMediaType("tv")} testid="genre-media-tv" />
            </div>
            {shown.length === 0 ? (
              <div className="mt-16 text-center text-[var(--text-secondary)]" data-testid="friends-favorites-filter-empty">
                No {mediaType === "movie" ? "movies" : "TV shows"} among your friends' favorites — try “All”.
              </div>
            ) : wheel ? (
              <><WheelBrowser rows={shown.map((it) => ({ ...cardRow(it, `Loved by ${it.friend_fav_count || 1} friend${(it.friend_fav_count || 1) > 1 ? "s" : ""}`), avatars: it.loved_by }))} title="Loved by Friends" testid="wheel-friends-favorites" onHold={(i) => setWho(shown[i] || null)} />
              <LovedByDialog item={who} open={!!who} onOpenChange={(o) => { if (!o) setWho(null); }} /></>
            ) : (
              <div className="mt-8 grid grid-cols-2 gap-4 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6" data-testid="friends-favorites-grid">
                {shown.map((it) => (
                  <div key={it.id} className="relative w-full [&>div]:w-full" data-testid={`friends-fav-item-${it.id}`}>
                    <PosterCard item={it} />
                    <LovedByStack item={it} />
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </main>
      <Disclaimer />
    </div>
  );
}
